/**
 * Les sources d'une affaire.
 *
 * **Une table, non six cartes.** La première version empilait une carte par
 * catégorie, chacune avec son en-tête et son vide : sur une affaire neuve, cela
 * faisait six blocs de deux cents pixels annonçant tous la même chose, et il
 * fallait défiler pour voir qu'il n'y avait rien. La référence pose une barre
 * d'actions, des pastilles de catégorie qui **filtrent**, et une seule table
 * dense — la question « ai-je le CCTP ? » se lit alors sur les pastilles, sans
 * défiler.
 *
 * Le retrait **détache l'avenir, jamais le passé** : les instantanés déjà
 * produits gardent la pièce, et les métrés qui en sont sortis restent
 * justifiables.
 */
import { useMemo, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { CATEGORIES, type Piece, type Travail } from "../../api/types";
import { DetailDeLaPiece } from "./DetailDeLaPiece";
import { Avis } from "../../composants/avis";
import { useModale } from "../../composants/clavier";
import { Chargement, Echec, Vide } from "../../composants/etats";
import { Etat } from "../../composants/etiquettes";
import { poids, quand } from "../../composants/formats";
import { useDonnees, useSuivreLaFile, type Veille } from "../../composants/donnees";
import { useParametre } from "../../routeur";

/** Ce qu'un geste de lot rend, pièce par pièce.
 *
 * **Déclaré, non deviné.** Un `Record<string, …>` aurait laissé passer un
 * champ absent jusqu'au rendu — la leçon de la complétude vide, qui coûtait
 * une page blanche. Ici le compilateur exige que chaque liste existe. */
interface Ecartee {
  document_id: string;
  nom: string;
  motif: string;
}

interface Nommee {
  document_id: string;
  nom: string;
}

interface IssueDeRechargement {
  relancees: Nommee[];
  inchangees: Nommee[];
  refusees: Ecartee[];
}

interface IssueDeRetrait {
  retirees: Nommee[];
  refusees: Ecartee[];
  /** Ce que le retrait **ne défait pas** : les instantanés déjà servis ou
   *  prêts gardent la pièce. Le taire laisserait croire à un retrait complet
   *  — et sur un instantané prêt, l'activer la remettrait en service.
   *
   *  **Optionnel, parce que le fil peut ne pas le porter.** Un portail plus
   *  ancien que cet écran répond sans ce champ ; le déclarer obligatoire l'a
   *  fait lire sur `undefined`, et une suppression pourtant réussie s'est
   *  annoncée « n'a pas abouti ». Le type doit dire ce qui arrive, non ce
   *  qu'on espère. */
  retenues_par_un_instantane?: {
    nom: string;
    instantanes: { generation: number; etat: string }[];
  }[];
}

/** Les états d'instantané, dits au métreur. Un « SUPERSEDED » sur un écran
 *  français est le nom d'une colonne, pas une information. */
const MOT_ETAT: Record<string, string> = {
  DRAFT: "brouillon", PROCESSING: "en préparation", READY: "prêt",
  ACTIVE: "actif", SUPERSEDED: "dépassé", FAILED: "en échec",
};

export function Documents({
  affaireId,
  etat,
  veille,
  ouvrirOneDrive,
}: {
  affaireId: string;
  etat: string;
  veille: Veille<Travail>;
  ouvrirOneDrive: (categorie: string) => void;
}) {
  // **Une affaire non prête ne reçoit rien**, et le portail le refuse. Offrir
  // le bouton quand même faisait cliquer pour rien, puis lire un refus — au
  // mieux explicable, jamais utile. On le dit avant, et on n'offre pas.
  const recoit = etat === "READY";

  // **Ce qu'on regarde va dans l'adresse.** La catégorie filtrée, le terme
  // cherché et la pièce ouverte disent où l'on en est dans deux cents pièces :
  // les perdre au rechargement oblige à refaire le chemin, et le lien qu'on
  // envoie ne montre pas ce qu'on voulait montrer.
  const [categorie, poserCategorie] = useParametre("cat");
  const [filtre, poserFiltre] = useParametre("q");
  const [detail, poserDetail] = useParametre("piece");

  // **Et ce qui n'y va pas.** Un retrait à confirmer, un rechargement en vol,
  // un message : ressusciter une confirmation à demi engagée par un F5 serait
  // pire que la perdre — on rouvrirait « retirer cette pièce ? » sans que
  // personne ne l'ait demandé cette fois-ci.
  // **La sélection ne va pas dans l'adresse.** Elle n'est ni « où l'on est »
  // ni un réglage de confort : c'est un geste en cours. Un lien qui rouvrirait
  // douze pièces cochées ferait cliquer « Retirer » sur une sélection qu'on
  // n'a pas faite soi-même.
  const [prises, poserPrises] = useState<Set<string>>(new Set());
  const [lot, poserLot] = useState("");
  const [rapport, poserRapport] = useState<string[]>([]);
  const [retraitEnLot, poserRetraitEnLot] = useState(false);
  const dernierePrise = useRef<string | null>(null);

  const [retrait, poserRetrait] = useState<Piece | null>(null);
  const [recharge, poserRecharge] = useState<string | null>(null);
  const [avis, poserAvis] = useState("");
  const [erreur, poserErreur] = useState("");

  // Modales complètes : Échap ferme, focus piégé et rendu — pas pendant un
  // retrait en cours, où fermer masquerait une opération engagée.
  const refLot = useModale(retraitEnLot && lot === "",
                           () => poserRetraitEnLot(false));
  const refRetrait = useModale(retrait !== null && lot === "",
                               () => poserRetrait(null));

  const pieces = useDonnees<Piece[]>(
    () => api.toutesLesPages<Piece>(`/api/v1/affaires/${affaireId}/pieces?taille=100`),
    [affaireId],
  );

  // Une pièce qui descend passe par plusieurs états, et sa révision n'existe
  // qu'à la fin : sans cela, la liste montrait « 0 révision » sur un import
  // terminé depuis longtemps.
  useSuivreLaFile(veille.empreinte, pieces.recharger);

  const toutes = pieces.donnees ?? [];

  const comptes = useMemo(() => {
    const tas: Record<string, number> = {};
    for (const piece of toutes) tas[piece.categorie] = (tas[piece.categorie] ?? 0) + 1;
    return tas;
  }, [toutes]);

  const visibles = useMemo(() => {
    const terme = filtre.trim().toLowerCase();
    return toutes.filter(
      (piece) =>
        (!categorie || piece.categorie === categorie) &&
        // Le filtre de fichiers porte sur **ce qui est déjà à l'écran** : les
        // pièces d'une affaire se comptent en dizaines et sont toutes
        // chargées, contrairement à la liste des affaires.
        (!terme ||
          piece.nom.toLowerCase().includes(terme) ||
          (piece.dossier ?? "").toLowerCase().includes(terme)),
    );
  }, [toutes, categorie, filtre]);

  /** Relire à la source, et importer **si la version a changé**.
   *
   * Les deux issues se ressemblent à l'écran — rien ne bouge dans les deux cas
   * pendant quelques secondes. Les distinguer par un mot est tout ce qui
   * sépare « c'est à jour » de « attendez, ça descend ». */
  async function recharger(piece: Piece) {
    poserErreur("");
    poserAvis("");
    poserRecharge(piece.document_id);
    try {
      const issue = await api.ecrire<{ donnees: { nouvelle_version: boolean } }>(
        `/api/v1/pieces/${piece.document_id}/recharger`,
      );
      poserAvis(
        issue.donnees.nouvelle_version
          ? `Nouvelle version de « ${piece.nom} » : l'import est lancé.`
          : `« ${piece.nom} » est déjà à jour — rien n'a changé à la source.`,
      );
      pieces.recharger();
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "La relecture n'a pas abouti.",
      );
    } finally {
      poserRecharge(null);
    }
  }

  /** Prend ou lâche une pièce ; avec Maj, toute la plage depuis la dernière.
   *
   * Même geste que dans le navigateur OneDrive, et c'est voulu : deux façons
   * de sélectionner dans la même application s'apprennent deux fois. */
  function prendre(piece: Piece, plage = false) {
    // **La plage se calcule ici, non dans la mise à jour d'état.** L'updater
    // de `useState` est différé : React l'exécute au rendu suivant, donc
    // *après* la ligne qui réécrit `dernierePrise`. Lu dedans, l'ancrage
    // valait déjà la pièce qu'on vient de cliquer — la plage se réduisait à
    // elle-même, et Maj+clic ne prenait que deux lignes au lieu de quatre.
    const rang = visibles.findIndex((p) => p.document_id === piece.document_id);
    const depuis = dernierePrise.current
      ? visibles.findIndex((p) => p.document_id === dernierePrise.current)
      : -1;
    const cible =
      plage && depuis !== -1 && rang !== -1
        ? visibles.slice(Math.min(depuis, rang), Math.max(depuis, rang) + 1)
        : [piece];
    const veut = !prises.has(piece.document_id);

    poserPrises((deja) => {
      const apres = new Set(deja);
      for (const p of cible) {
        if (veut) apres.add(p.document_id);
        else apres.delete(p.document_id);
      }
      return apres;
    });
    dernierePrise.current = piece.document_id;
  }

  const choisies = visibles.filter((p) => prises.has(p.document_id));
  const toutesPrises = visibles.length > 0 && choisies.length === visibles.length;

  /** Un geste de lot : son appel, son compte rendu, et la liste rafraîchie.
   *
   * **Le compte rendu nomme.** « 3 pièces refusées » oblige à recommencer une
   * par une pour savoir laquelle bloque. */
  async function enLot<T>(
    quoi: string,
    chemin: string,
    dire: (issue: T) => string[],
  ) {
    poserErreur("");
    poserAvis("");
    poserRapport([]);
    poserLot(quoi);
    try {
      const issue = await api.ecrire<{ donnees: T }>(
        `/api/v1/affaires/${affaireId}/pieces/${chemin}`,
        { document_ids: [...prises] },
      );
      // **Le geste est fait ; le reste n'est que de l'habillage.** Composer le
      // message se fait donc à part : une phrase qu'on n'arrive pas à écrire
      // ne doit pas faire annoncer l'échec d'une écriture que le serveur a
      // acceptée. C'est exactement ce qui s'est produit avec un portail plus
      // ancien que cet écran.
      poserPrises(new Set());
      pieces.recharger();
      try {
        const lignes = dire(issue.donnees);
        poserAvis(lignes[0] ?? "");
        poserRapport(lignes.slice(1));
      } catch {
        poserAvis("Le geste a abouti.");
      }
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "Le geste n'a pas abouti.",
      );
    } finally {
      poserLot("");
    }
  }

  /** Télécharge les pièces choisies en une archive.
   *
   * **Un POST pour une lecture.** Deux cents identifiants dans une adresse
   * dépassent ce que les intermédiaires acceptent ; et l'archive revient en
   * mémoire plutôt que par une navigation, ce qui permet d'afficher un refus
   * *dans* l'application au lieu de remplacer la page par du JSON brut. */
  async function telecharger() {
    poserErreur("");
    poserAvis("");
    poserRapport([]);
    poserLot("archive");
    try {
      const reponse = await fetch(
        `/api/v1/affaires/${affaireId}/pieces/archive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ document_ids: [...prises] }),
        },
      );
      if (!reponse.ok) {
        const souci = await reponse.json().catch(() => ({}));
        throw new Error(souci.detail || "L'archive n'a pas pu être assemblée.");
      }
      const ecartees = Number(reponse.headers.get("X-Pieces-Ecartees") ?? 0);
      const enveloppe = await reponse.blob();
      const adresse = URL.createObjectURL(enveloppe);
      const lien = document.createElement("a");
      lien.href = adresse;
      lien.download = (reponse.headers.get("content-disposition") ?? "")
        .match(/filename\*=UTF-8''([^;]+)/)?.[1]
        ? decodeURIComponent(RegExp.$1)
        : "pieces.zip";
      lien.click();
      // Révoquer trop tôt annule le téléchargement en cours dans Safari.
      window.setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
      poserAvis(
        ecartees
          ? `Archive téléchargée. ${ecartees} pièce(s) écartée(s) faute de `
            + "fichier disponible."
          : "Archive téléchargée.",
      );
      poserPrises(new Set());
    } catch (exc) {
      poserErreur(
        exc instanceof Error ? exc.message : "Le téléchargement n'a pas abouti.",
      );
    } finally {
      poserLot("");
    }
  }

  async function retirer(piece: Piece) {
    if (lot !== "") return;  // un retrait déjà en cours : pas de double DELETE
    poserErreur("");
    poserAvis("");
    poserRapport([]);
    poserLot("retrait");
    try {
      const issue = await api.retirer<{
        donnees?: { retenue_par?: { generation: number; etat: string }[] };
      }>(`/api/v1/pieces/${piece.document_id}`);
      // Le retrait est acquis dès que le portail a répondu sans refus. Ce qui
      // suit décore, et ne peut plus le remettre en cause.
      poserRetrait(null);
      pieces.recharger();
      const retenue = issue?.donnees?.retenue_par ?? [];
      // **Ce que le geste tient vraiment.** « Ne sera plus lue par les
      // prochaines missions » était faux tant qu'aucun instantané neuf n'était
      // activé : une mission lit le miroir, rafraîchi depuis l'instantané
      // *actif*, qui porte encore la pièce. Le retrait agit sur le prochain,
      // pas sur celui en service.
      poserAvis(
        retenue.length === 0
          ? `« ${piece.nom} » est retirée du prochain instantané.`
          : `« ${piece.nom} » est retirée du prochain instantané, mais reste `
            + `en service tant que celui-ci n'est pas activé.`,
      );
      poserRapport(retenue.map(
        (i) => `Génération ${i.generation} (${MOT_ETAT[i.etat] ?? i.etat})`));
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "Le retrait n'a pas abouti.",
      );
    } finally {
      poserLot("");
    }
  }

  if (pieces.charge && !pieces.donnees) return <div className="contenu"><Chargement lignes={4} /></div>;
  if (pieces.erreur) {
    return (
      <div className="contenu">
        <Echec erreur={pieces.erreur} reessayer={pieces.recharger} />
      </div>
    );
  }

  return (
    <div className="sous-ecran">
      <div className="sous-ecran__tete">
        {!recoit && (
          <div className="avis">
            {etat === "PROVISIONING"
              ? "L'espace de cette affaire est en cours de préparation. "
                + "L'ajout de sources sera possible dès qu'elle sera prête."
              : etat === "ARCHIVED"
                ? "Cette affaire est archivée : elle se consulte, "
                  + "elle ne se modifie plus."
                : `Cette affaire n'accepte pas de source (${etat}).`}
          </div>
        )}

        <div className="outils outils--serres">
          <button
            type="button"
            className="bouton bouton--primaire"
            disabled={!recoit}
            onClick={() => ouvrirOneDrive(categorie || "CCTP")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M7 18h11a3.5 3.5 0 0 0 .3-7 5.5 5.5 0 0 0-10.5-1.4A4 4 0 0 0 7 18Z" />
            </svg>
            Ajouter depuis OneDrive
          </button>
          <button type="button" className="bouton" onClick={() => pieces.recharger()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M20 11A8 8 0 0 0 6.3 6.3L3 9" />
              <path d="M3 4v5h5" />
              <path d="M4 13a8 8 0 0 0 13.7 4.7L21 15" />
              <path d="M21 20v-5h-5" />
            </svg>
            Actualiser
          </button>

          <label className="champ-recherche champ-recherche--court">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="search"
              value={filtre}
              placeholder="Filtrer les fichiers…"
              aria-label="Filtrer les fichiers"
              onChange={(evenement) => poserFiltre(evenement.target.value, true)}
            />
          </label>
        </div>

        {/* Les pastilles disent la complétude **et** filtrent. Une catégorie
            vide reste offerte : c'est là qu'on ajoute ce qui manque. */}
        <div className="pastilles">
          <button
            type="button"
            className={`pastille-cat${categorie === "" ? " pastille-cat--active" : ""}`}
            onClick={() => poserCategorie("", true)}
          >
            Toutes <span className="mono">{toutes.length}</span>
          </button>
          {CATEGORIES.map((item) => (
            <button
              key={item.clef}
              type="button"
              title={item.extensions}
              className={
                `pastille-cat${categorie === item.clef ? " pastille-cat--active" : ""}` +
                (comptes[item.clef] ? "" : " pastille-cat--vide")
              }
              onClick={() => poserCategorie(item.clef, true)}
            >
              {item.libelle} <span className="mono">{comptes[item.clef] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="sous-ecran__corps">
        {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
        {avis && (
          <Avis ton="info" cle={avis}>
            <div className="pile pile--serree">
              <span>{avis}</span>
              {/* **Le détail nomme.** Un total dit qu'il y a eu un souci ;
                  seul le nom dit lequel des vingt-quatre fichiers le porte. */}
              {rapport.map((ligne) => (
                <span key={ligne} className="discret">{ligne}</span>
              ))}
            </div>
          </Avis>
        )}

        {/* **La barre n'apparaît qu'avec une sélection**, et se place au-dessus
            de la liste plutôt que dans le bandeau : elle appartient aux lignes
            cochées, et les gestes qu'elle porte doivent être près d'elles. */}
        {choisies.length > 0 && (
          <div className="lot">
            <span className="lot__compte">
              {choisies.length} pièce{choisies.length > 1 ? "s" : ""} sélectionnée
              {choisies.length > 1 ? "s" : ""}
            </span>
            <button type="button" className="bouton bouton--discret"
                    onClick={() => poserPrises(new Set())}>
              Tout désélectionner
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="bouton"
              disabled={lot !== ""}
              aria-label="Recharger les pièces sélectionnées"
              onClick={() =>
                void enLot<IssueDeRechargement>("rechargement", "rechargement", (issue) => [
                  `${issue.relancees.length} pièce(s) mise(s) à jour, `
                  + `${issue.inchangees.length} déjà à jour`
                  + (issue.refusees.length
                      ? `, ${issue.refusees.length} refusée(s).` : "."),
                  ...issue.refusees.map((r) => `${r.nom} — ${r.motif}`),
                ])
              }
            >
              {lot === "rechargement" ? "Relecture…" : "Recharger"}
            </button>
            <button
              type="button"
              className="bouton"
              disabled={lot !== ""}
              aria-label="Télécharger les pièces sélectionnées"
              onClick={() => void telecharger()}
            >
              {lot === "archive" ? "Assemblage…" : "Télécharger"}
            </button>
            <button
              type="button"
              className="bouton bouton--danger"
              disabled={lot !== ""}
              aria-label="Retirer les pièces sélectionnées"
              onClick={() => poserRetraitEnLot(true)}
            >
              Retirer
            </button>
          </div>
        )}

        {visibles.length === 0 ? (
          <div className="carte">
            <Vide
              titre={
                toutes.length === 0
                  ? "Aucune source dans cette affaire"
                  : "Aucune pièce ne correspond"
              }
              aide={
                toutes.length === 0
                  ? "Ajoutez un fichier depuis OneDrive pour démarrer."
                  : "Changez de catégorie, ou effacez le filtre."
              }
            />
          </div>
        ) : (
          <div className="liste liste--pieces">
            <div className="liste__entete">
              <span>
                <input
                  type="checkbox"
                  checked={toutesPrises}
                  ref={(n) => {
                    if (n) n.indeterminate = !toutesPrises && choisies.length > 0;
                  }}
                  onChange={() =>
                    poserPrises(
                      toutesPrises
                        ? new Set()
                        : new Set(visibles.map((p) => p.document_id)),
                    )
                  }
                  aria-label={
                    toutesPrises
                      ? "Désélectionner toutes les pièces"
                      : "Sélectionner toutes les pièces affichées"
                  }
                />
              </span>
              <span>Fichier</span>
              <span>Dossier OneDrive</span>
              <span>Modifié</span>
              <span>Rév.</span>
              <span>Taille</span>
              <span>Traitement</span>
              <span />
            </div>
            {visibles.map((piece) => (
              <div
                key={piece.document_id}
                className={
                  "liste__ligne liste__ligne--fixe"
                  + (prises.has(piece.document_id) ? " liste__ligne--visee" : "")
                }
              >
                <span>
                  <input
                    type="checkbox"
                    checked={prises.has(piece.document_id)}
                    onChange={() => undefined}
                    onClick={(evenement) => prendre(piece, evenement.shiftKey)}
                    aria-label={`Sélectionner ${piece.nom}`}
                  />
                </span>
                <span className="fichier">
                  <span className="fichier__type">{extension(piece.nom)}</span>
                  <span className="mono tronque">{piece.nom}</span>
                </span>
                <span className="discret tronque">{piece.dossier || "—"}</span>
                <span className="discret mono">{quand(piece.modifie_le)}</span>
                <span className="mono">{piece.revisions ?? 0}</span>
                <span className="discret mono">
                  {piece.octets ? poids(piece.octets) : "—"}
                </span>
                <Etat genre="piece" valeur={piece.etat} />
                <span className="actions">
                  <button
                    type="button"
                    className="action"
                    title="Détails et révisions"
                    aria-label={`Détails de ${piece.nom}`}
                    onClick={() => poserDetail(piece.document_id)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 11v5" />
                      <path d="M12 8h.01" />
                    </svg>
                  </button>

                  {/* **Un vrai lien, non un `fetch`.** Le navigateur sait
                      enregistrer un flux de deux cent cinquante mégaoctets ;
                      le passer par `fetch` le chargerait d'abord en mémoire
                      pour le rendre ensuite, ce qui n'apporte rien et coûte
                      la taille du fichier. Le cookie part avec, l'autorisation
                      est vérifiée au serveur. */}
                  <a
                    className={`action${piece.revisions ? "" : " action--eteinte"}`}
                    title={
                      piece.revisions
                        ? "Télécharger la version courante"
                        : "Rien à télécharger : la pièce n'est pas encore descendue"
                    }
                    aria-label={`Télécharger ${piece.nom}`}
                    aria-disabled={!piece.revisions}
                    href={
                      piece.revisions
                        ? `/api/v1/pieces/${piece.document_id}/contenu`
                        : undefined
                    }
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M12 3v12" />
                      <path d="m7 10 5 5 5-5" />
                      <path d="M4 20h16" />
                    </svg>
                  </a>

                  <button
                    type="button"
                    className="action"
                    title="Relire à la source et importer si la version a changé"
                    aria-label={`Recharger ${piece.nom}`}
                    disabled={recharge === piece.document_id || !recoit}
                    onClick={() => void recharger(piece)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M20 11A8 8 0 0 0 6.3 6.3L3 9" />
                      <path d="M3 4v5h5" />
                      <path d="M4 13a8 8 0 0 0 13.7 4.7L21 15" />
                      <path d="M21 20v-5h-5" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className="bouton bouton--discret"
                    onClick={() => poserRetrait(piece)}
                  >
                    Retirer
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* **La note permanente est partie, et rien n'est perdu.** Elle
            expliquait la conséquence d'un retrait — « les instantanés déjà
            produits la conservent, rien n'est effacé » — sous chaque liste et
            pour toujours, soit la place de deux fichiers à chaque affichage.
            La boîte de confirmation dit la même chose, sur le fichier
            concerné, au moment où la question se pose. Une explication
            affichée en permanence n'est pas lue deux fois plus, elle est lue
            deux fois moins. */
        }
      </div>

      {detail && (
        <DetailDeLaPiece
          documentId={detail}
          fermer={() => poserDetail("", true)}
        />
      )}

      {/* **Une confirmation, comme pour une seule pièce — et qui compte.**
          Retirer douze sources d'un clic sans rien demander serait le seul
          geste destructif du portail sans garde-fou. */}
      {retraitEnLot && (
        <div ref={refLot} className="voile" role="dialog" aria-modal="true"
             aria-label="Retirer des sources">
          <div className="modale" style={{ width: "min(520px, 100%)" }}>
            <div className="modale__tete">
              <h2>Retirer {choisies.length} source(s) de l'affaire ?</h2>
            </div>
            <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <ul className="lot__liste">
                  {choisies.slice(0, 8).map((piece) => (
                    <li key={piece.document_id} className="tronque">{piece.nom}</li>
                  ))}
                  {choisies.length > 8 && (
                    <li className="discret">
                      et {choisies.length - 8} autre(s).
                    </li>
                  )}
                </ul>
                <Avis ton="attention" permanent>
                  Elles restent <strong>en service</strong> tant que le
                  prochain instantané n'est pas préparé puis activé : une
                  mission lit l'instantané actif, pas la liste des pièces.
                </Avis>
                <Avis ton="info" permanent>
                  Les instantanés déjà produits les conservent, et les métrés
                  qui en sont sortis restent justifiables. Rien n'est effacé.
                </Avis>
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserRetraitEnLot(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="bouton bouton--danger"
                disabled={lot !== ""}
                onClick={() => {
                  poserRetraitEnLot(false);
                  void enLot<IssueDeRetrait>("retrait", "retrait", (issue) => [
                    `${issue.retirees.length} pièce(s) retirée(s)`
                    + (issue.refusees.length
                        ? `, ${issue.refusees.length} refusée(s).` : "."),
                    ...issue.refusees.map((r) => `${r.nom} — ${r.motif}`),
                    ...(issue.retenues_par_un_instantane ?? []).map(
                      (p) => `${p.nom} reste dans ${p.instantanes.length} `
                             + "instantané(s) déjà figé(s)."),
                  ]);
                }}
              >
                {lot === "retrait" ? "Retrait…" : "Retirer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {retrait && (
        <div ref={refRetrait} className="voile" role="dialog" aria-modal="true" aria-label="Retirer une source">
          <div className="modale" style={{ width: "min(480px, 100%)" }}>
            <div className="modale__tete"><h2>Retirer une source de l'affaire ?</h2></div>
            <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <p>
                  <strong>{retrait.nom}</strong> sort du{" "}
                  <strong>prochain</strong> instantané.
                </p>
                <div className="avis avis--attention">
                  Il reste <strong>en service</strong> tant que ce prochain
                  instantané n'est pas préparé puis activé : une mission lit
                  l'instantané actif, pas la liste des pièces.
                </div>
                <div className="avis avis--info">
                  Les instantanés déjà produits le conservent, et les métrés qui
                  en sont sortis restent justifiables. Rien n'est effacé.
                </div>
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton" onClick={() => poserRetrait(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="bouton bouton--danger"
                disabled={lot !== ""}
                onClick={() => retirer(retrait)}
              >
                {lot === "retrait" ? "Retrait…" : "Retirer la source"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Le suffixe, en trois lettres au plus — c'est ce que la référence met dans
 * la vignette. Un nom sans point rend « ? » plutôt qu'une chaîne vide. */
function extension(nom: string): string {
  const point = nom.lastIndexOf(".");
  if (point < 0 || point === nom.length - 1) return "?";
  return nom.slice(point + 1, point + 5).toUpperCase();
}
